import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

export default function Checkout() {
  const { bookId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [book, setBook] = useState(null);
  const [config, setConfig] = useState(null);
  const [email, setEmail] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: cfg }, { data: booksResp }] = await Promise.all([
        api.get("/checkout/config"),
        api.get("/books"),
      ]);
      setConfig(cfg);
      const b = (booksResp.books || []).find((x) => x.id === bookId);
      setBook(b);
      if (user?.email) setEmail(user.email);
    })();
  }, [bookId, user]);

  const validateEmail = () => {
    if (!user && !email) {
      toast.error("Please enter your email so we can deliver the PDF.");
      return false;
    }
    return true;
  };

  const createPayPalOrder = async () => {
    const payload = user ? { book_id: book.id } : { book_id: book.id, email };
    const { data: order } = await api.post("/orders/create", payload);
    sessionStorage.setItem("current_order_id", order.order_id);
    return order.paypal_order_id;
  };

  const onPayPalApprove = async (data) => {
    const localOrderId = sessionStorage.getItem("current_order_id");
    if (!localOrderId) throw new Error("Missing local order id");
    setProcessing(true);
    try {
      await api.post("/orders/capture", {
        order_id: localOrderId,
        paypal_order_id: data.orderID,
      });
      sessionStorage.removeItem("current_order_id");
      toast.success("Payment complete — check your email.");
      nav(`/order-success/${localOrderId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment capture failed");
    } finally {
      setProcessing(false);
    }
  };

  const handleMockPay = async () => {
    if (!validateEmail()) return;
    setProcessing(true);
    try {
      const payload = user ? { book_id: book.id } : { book_id: book.id, email };
      const { data: order } = await api.post("/orders/create", payload);
      await api.post("/orders/capture", { order_id: order.order_id, mock: true });
      toast.success("Payment complete — check your email.");
      nav(`/order-success/${order.order_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally {
      setProcessing(false);
    }
  };

  if (!book) return <div className="min-h-screen flex items-center justify-center"><p className="eyebrow">Preparing checkout</p></div>;

  return (
    <div className="max-w-[1360px] mx-auto px-6 lg:px-12 pt-16 pb-24 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-7">
        <p className="eyebrow">Checkout</p>
        <h1 className="font-serif-display text-5xl sm:text-6xl leading-none tracking-tighter mt-6">
          You're about to receive<br/><em className="italic">a real book.</em>
        </h1>
        <p className="mt-6 text-[#5C5C5C] max-w-lg leading-relaxed">
          After payment, we'll email your PDF instantly. No account required. No subscriptions. No hidden charges.
        </p>

        <div className="mt-12 border border-[#E5E2DC] p-8 bg-white">
          <p className="eyebrow">Order summary</p>
          <div className="mt-6 flex gap-6 items-start">
            <div className="w-24 aspect-[3/4] overflow-hidden bg-[#E5E2DC] flex-shrink-0">
              <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <h2 className="font-serif-display text-2xl leading-tight">{book.title}</h2>
              <p className="italic font-serif-display text-[#5C5C5C]">{book.subtitle}</p>
              <p className="text-sm text-[#5C5C5C] mt-2">Digital PDF · Instant delivery</p>
            </div>
            <div className="text-right">
              <p className="font-serif-display text-3xl" data-testid="checkout-price">${book.price?.toFixed(2)}</p>
            </div>
          </div>
          <div className="hairline my-8" />
          <div className="flex justify-between items-baseline">
            <p className="eyebrow">Total</p>
            <p className="font-serif-display text-4xl">${book.price?.toFixed(2)}</p>
          </div>
        </div>
      </div>

      <div className="lg:col-span-5">
        <div className="border border-[#E5E2DC] p-8 bg-white lg:sticky lg:top-28">
          <p className="eyebrow">Deliver PDF to</p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={!!user}
            required
            placeholder="you@somewhere.com"
            data-testid="checkout-email"
            className="mt-2 w-full bg-[#F9F8F6] border border-[#E5E2DC] px-3 py-3 text-sm outline-none focus:border-[#2A3B32] disabled:opacity-70"
          />
          {user && <p className="text-xs text-[#5C5C5C] mt-2">Signed in as {user.email}</p>}

          <div className="hairline my-6" />

          <p className="eyebrow">Payment</p>
          {config?.paypal_enabled && config?.paypal_client_id ? (
            <div className="mt-4" data-testid="paypal-buttons-container">
              <p className="text-xs text-[#5C5C5C] mb-3">Pay securely with your PayPal account.</p>
              <PayPalScriptProvider options={{
                "client-id": config.paypal_client_id,
                currency: "USD",
                intent: "capture",
                "disable-funding": "card,credit,paylater",
              }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                  disabled={processing}
                  forceReRender={[email, book.id]}
                  onClick={(_, actions) => {
                    if (!validateEmail()) return actions.reject();
                    return actions.resolve();
                  }}
                  createOrder={createPayPalOrder}
                  onApprove={onPayPalApprove}
                  onError={(err) => {
                    console.error("PayPal error", err);
                    toast.error("Payment failed. Please try again.");
                  }}
                  onCancel={() => {
                    toast("Payment cancelled");
                  }}
                />
              </PayPalScriptProvider>
              {processing && <p className="text-xs text-[#5C5C5C] mt-3 text-center">Finalizing your order…</p>}
            </div>
          ) : (
            <>
              <div className="mt-4 border border-dashed border-[#B85C38]/40 bg-[#B85C38]/[0.04] p-4 text-xs text-[#5C5C5C] leading-relaxed">
                <strong className="text-[#1C1C1C]">Demo mode.</strong> PayPal is not configured. Add credentials in backend/.env to enable live checkout.
              </div>
              <button onClick={handleMockPay} disabled={processing} data-testid="mock-pay-button" className="btn-primary w-full justify-center mt-6">
                {processing ? "Processing…" : `Complete demo payment · $${book.price?.toFixed(2)}`}
              </button>
            </>
          )}

          <div className="flex items-center gap-2 mt-6 text-xs text-[#5C5C5C]">
            <Lock size={12} /> Secure payment · Your email is used only for delivery.
          </div>

          {config?.stripe_enabled && (
            <div className="mt-6 pt-6 border-t border-[#E5E2DC]">
              <p className="eyebrow mb-3">Card, Apple Pay, Google Pay & more</p>
              <button
                onClick={async () => {
                  if (!validateEmail()) return;
                  setProcessing(true);
                  // Detect if we're running inside an iframe/embed (e.g. Emergent preview).
                  // Stripe refuses to render inside iframes and would show a blank page.
                  const inIframe = (() => {
                    try { return window.self !== window.top; } catch { return true; }
                  })();
                  const topOrigin = (() => {
                    try { return window.top.location.origin; } catch { return window.location.origin; }
                  })();
                  try {
                    const { data } = await api.post("/stripe/checkout", {
                      book_id: book.id,
                      email,
                      origin_url: topOrigin,
                    });
                    if (inIframe) {
                      // Open in a new top-level tab so Stripe can render properly
                      window.open(data.url, "_blank", "noopener,noreferrer");
                      toast.success("Stripe checkout opened in a new tab.");
                      setProcessing(false);
                    } else {
                      window.location.href = data.url;
                    }
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Stripe error");
                    setProcessing(false);
                  }
                }}
                disabled={processing}
                data-testid="stripe-pay-button"
                className="w-full py-3.5 px-6 text-[12px] tracking-[0.16em] uppercase font-medium bg-[#635BFF] hover:bg-[#4F46E5] text-white transition-colors disabled:opacity-60"
              >
                {processing ? "Opening Stripe…" : `Pay by card / wallet · $${book.price?.toFixed(2)}`}
              </button>
              <p className="text-[11px] text-[#5C5C5C] mt-3 leading-relaxed">
                Includes Apple Pay, Google Pay, Stripe Link, Klarna, iDEAL, Bancontact & more — shown automatically based on your device and location. If viewed inside a preview window, opens in a new tab.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
